<template>
  <SListItem class="absence-item info-dark"
             :badges="getBadges"
             :class="[{ 'absence-deleted': !!absence.deleted_at }]"
             hoverable
  >
    <template #item-title="{ ariaDescribedby }">
      <!-- eslint-disable-next-line sdui/require-focus-restore-on-disabled-button -->
      <b-button
        :id="`edit-absence-${absence.uuid}`"
        variant="transparent"
        :disabled="!displayOptions"
        :aria-describedby="ariaDescribedby"
        class="reset-button sdui-list-item__click-area"
        @click="toggleDetailsModal"
      >
        <span class="btn-toggle-absence-detail-modal-displayname">{{ absence.user.meta.displayname }}</span>
        <sdui-icon
          v-if="showCommentIcon"
          data-cy="comment-quotes"
          class="quoteIcon icon-quote-16 text-primary ml-1"
          bg-variant="white"
          variant="primary"
        />
      </b-button>
    </template>

    <template #description>
      <absence-duration-description :absence="absence"
                                    class="sdui-list-item__description nano text-muted"
                                    data-cy="absence-date-range"
      />
    </template>

    <template #right-action v-if="showListOptions">
      <classbook-list-item-actions
        :item="absence"
        :sr-only-descr="$t('classbook.absences.dropdown_button')"
        :show-edit-action="false"
        :show-spinner="showLoadingSpinner"
        @shown="toggleOptionsDropdown(true)"
        @hidden="toggleOptionsDropdown"
        @delete="showDeleteConfirmationModal = true"
        @history="showHistoryModal = true"
        @send-absence-notification="sendAbsencePushNotification"
      >
        <template #beforeItems>
          <li>
            <small class="btn d-block text-muted nano text-truncate text-left font-weight-normal">{{
              $t('classbook.absences.user_absences.identifier', { id: identifier })
            }}</small>
          </li>
        </template>
      </classbook-list-item-actions>
    </template>

    <template #modals>
      <SConfirmModal
        v-model="showDeleteConfirmationModal"
        :title="$t('classbook.absences.confirmModal.title')"
        :description="$t('classbook.absences.confirmModal.description')"
        :ok-title="$t('global.delete')"
        :cancel-title="$t('global.cancel')"
        :return-focus="`#editDropdown-${absence.uuid}__BV_toggle_`"
        @ok="deleteAbsence"
        @hide="showDeleteConfirmationModal = false"
      />

      <SModal
        v-model="showDetailsModal"
        :show-footer-gradient="false"
        :header-title="$t('classbook.absences.overview.absence')"
        @hide="onAbsenceModalHide"
      >
        <user-single-absence-details
          v-if="showDetailsModal"
          :absence-types="correctAbsenceTypes"
          :absence-categories="correctAbsenceGroups"
          :absence="absence"
          :current-school-year-dates="$me.school.getSchoolYearByClassbokId(classbookId)"
          :classbook="classbookId"
          :selected-user="absence.user"
          :can="entity"
          @has-pending-changes="(pendingChanges) => (hasPendingChanges = pendingChanges)"
          @close="closeAbsenceDetailsModal"
          @update="updateAbsence"
        >
          <template #afterHeader>
            <sdui-loader v-if="$wait.waiting('fetch-entity')" class="py-2" />
            <classbook-entity-item-display
              v-else
              class="mb-3"
              selected-entity="users"
              :hide-description="false"
              with-shadow
              :entity="entity"
            />
          </template>
        </user-single-absence-details>
      </SModal>

      <SModal
        v-model="showHistoryModal"
        :header-title="$t('classbook.absences.user_absences.history')"
        :return-focus="`#editDropdown-${absence.uuid}__BV_toggle_`"
        hide-footer
      >
        <user-absence-history-modal v-if="showHistoryModal" :absence="absence" />
      </SModal>

      <SConfirmModal
        v-model="isConfirmLosingChangesModalDisplayed"
        :title="$t('classbook.absences.close_modal.title')"
        :description="$t('classbook.absences.close_modal.description')"
        :ok-title="$t('classbook.absences.close_modal.submit')"
        :cancel-title="$t('classbook.absences.close_modal.cancel')"
        :prevent-focus-return-on-modal-close="hasConfirmedLosingChanges"
        @ok="
          () => {
            hasConfirmedLosingChanges = true;
            closeAbsenceDetailsModal();
          }
        "
        @hide="isConfirmLosingChangesModalDisplayed = false"
      />
    </template>
  </SListItem>
</template>

<script>
import { waitFor } from 'src/config/setup/plugins/waitFor/index.js'
import Classbooks from 'src/api/classbooks.js'
import entityMixin from 'src/mixins/miscellaneous/entityMixin.js'
import absenceStatusMixin from 'src/mixins/classbook/absenceStatusMixin.js'
import classbookStatusFlagMixin from 'src/mixins/classbook/classbookStatusFlagMixin.js'
import isMobileDeviceMixing from 'src/mixins/miscellaneous/isMobileDeviceMixin.js'
import ClassbookListItemActions from 'src/components/classbook/ClassbookListItemActions.vue'
import UserAbsenceHistoryModal from 'src/components/classbook/absences/UserAbsenceHistoryModal.vue'
import UserSingleAbsenceDetails from 'src/components/classbook/absences/UserSingleAbsenceDetails.vue'
import ClassbookEntityItemDisplay from 'src/components/classbook/ClassbookEntityItemDisplay.vue'
import AbsenceDurationDescription from 'src/components/classbook/absences/AbsenceDurationDescription.vue'
import constants from 'src/config/constants.ts'
import SduiLoader from 'src/components/miscellaneous/ui/common/SduiLoader.vue'
import clyTracking from 'src/config/services/countlyTracking/clyTracking.js'

export default {
  name: 'UserSingleAbsenceOverview',

  components: {
    SduiLoader,
    AbsenceDurationDescription,
    ClassbookListItemActions,
    ClassbookEntityItemDisplay,
    UserAbsenceHistoryModal,
    UserSingleAbsenceDetails,
  },

  mixins: [ entityMixin, isMobileDeviceMixing, absenceStatusMixin, classbookStatusFlagMixin],

  props: {
    displayOptions: {
      type: Boolean,
      default: true,
    },

    absenceTypes: {
      type: Object,
      default: () => ({}),
    },

    absenceCategories: {
      type: Object,
      default: () => ({}),
    },

    entry: {
      type: Object,
      default: () => ({}),
    },

    absence: {
      type: Object,
      default: () => ({}),
    },

    detailsOpen: {
      type: Boolean,
      default: false,
    },

    classbookId: {
      type: Number,
      default: null,
    },

    can: {
      type: Object,
      default: () => ({}),
    },
  },

  data: () => ({
    showDeleteConfirmationModal: false,
    showDetailsModal: false,
    showOptions: false,
    showHistoryModal: false,
    isConfirmLosingChangesModalDisplayed: false,
    entity: null,
    localTypes: {},
    hasPendingChanges: false,
    categoriesAbsences: {},
    typesAbsences: {},
    hasConfirmedLosingChanges: false,
  }),

  computed: {
    getBadges() {
      if (!this.absence) return []

      const badges = []
      if (this.gradeShortCut) badges.push({ text: this.gradeShortCut, color: 'secondary' })
      badges.push(this.getAbsenceStateBadgeByAbsence(this.absence))
      if (this.absenceState) badges.push(this.getClassbookStatusBadge(this.statusFlagTitle, this.absenceState))
      return badges
    },

    showLoadingSpinner() {
      if (!this.absence) return false
      return this.$wait.waiting(`absence-notification-${this.absence.uuid}`) || this.$wait.waiting(`refresh-absence-${this.absence.uuid}`)
    },

    correctAbsenceTypes() {
      if (Object.keys(this.absenceTypes).length) return this.absenceTypes
      return this.localTypes.length ? this.localTypes : this.typesAbsences
    },

    correctAbsenceGroups() {
      if (Object.keys(this.absenceCategories).length) return this.absenceCategories
      return this.categoriesAbsences
    },

    identifier() {
      return this.absence.uuid.substring(0, 5)
    },

    gradeShortCut() {
      return this.absence.user?.grade?.shortcut
    },

    showCommentIcon() {
      return !!this.absence.excuse_note || !!this.absence.comment
    },

    showListOptions() {
      if (!this.absence.uuid || !this.displayOptions) return false
      return this.$can('update', this.absence) || this.$can('delete', this.absence) || this.$can('view-history', this.absence)
    },

    selectedAbsence() {
      return this.$route.query.selectedAbsence
    },
  },

  emits: ['click', 'delete', 'update', 'refetchAbsence'],

  methods: {
    getEntity: waitFor('fetch-entity', async function () {
      this.entity = await this.fetchEntity('users', this.absence.user_id, this.absence.classbook_id)
    }),

    async deleteAbsence() {
      try {
        await Classbooks.deleteAbsencesById(this.absence.classbook_id, this.absence.uuid)
        clyTracking.trackClassbook('absence').end.delete()
      } catch (e) {
        clyTracking.trackClassbook('absence', 'FAILED', e.message).end.delete()
        throw e
      }
      this.$emit('delete', this.absence)
    },

    toggleDetailsModal() {
      if (!this.showDetailsModal) {
        this.$emit('click', this.absence.user_id)
      }
      clyTracking.trackClassbook('absence').start.edit()
      this.showDetailsModal = !this.showDetailsModal
    },

    toggleOptionsDropdown(open = false) {
      this.showOptions = open
    },

    updateAbsence(absence) {
      this.showDetailsModal = false
      this.hasPendingChanges = false
      this.$emit('update', absence)
    },

    closeAbsenceDetailsModal() {
      if (!this.absence.uuid) {
        this.$emit('update', this.absence)
      }

      this.hasPendingChanges = false
      this.showDetailsModal = false
    },

    async onAbsenceModalHide(event) {
      if (!this.hasPendingChanges) {
        this.closeAbsenceDetailsModal()
        return
      }

      event?.preventDefault()

      this.isConfirmLosingChangesModalDisplayed = true
    },

    async sendAbsencePushNotification() {
      try {
        this.$wait.start(`absence-notification-${this.absence.uuid}`)
        await Classbooks.postAbsenceNotification(this.absence.classbook_id, { absences_uuids: [this.absence.uuid] })
        this.$emit('refetchAbsence', this.absence)
      } finally {
        this.$wait.end(`absence-notification-${this.absence.uuid}`)
      }
    },
  },

  watch: {
    showDetailsModal: {
      handler: async function (val) {
        if (!val) return
        await this.getEntity()
      },
    },

    detailsOpen: {
      handler(val) {
        if (val === this.showDetailsModal) return
        this.showDetailsModal = val
      },

      immediate: true,
    },

    selectedAbsence: {
      async handler(uuid) {
        if (!uuid || uuid !== this.absence.uuid) return

        // When absence details modal is opened from a push notification
        // the absence categories and types are not fetched
        const categories = await Classbooks.getAbsenceCategories(this.classbookId)
        this.categoriesAbsences = categories.data
        const types = await Classbooks.getAbsenceTypesByEntityNameEntityId(this.classbookId, 'users', this.absence.user_id)
        this.typesAbsences = types.data

        this.showDetailsModal = true
        this.$router.replace({ query: { selectedAbsence: undefined, activeTab: constants.classbook.absences.filter.ALL } })
      },

      immediate: true,
    },

    showHistoryModal(newVal) {
      if (!newVal) return
      clyTracking.trackClassbook('absence').interaction.changeHistory()
    },

    showDeleteConfirmationModal(newVal) {
      if (!newVal) return
      clyTracking.trackClassbook('absence').start.delete()
    },
  },
}
</script>

<style scoped lang="scss">
.quoteIcon {
  box-shadow: $sdui-shadow;
  height: 16px;
  width: 16px;
  transform: translateY(-1px);
}

.absence-deleted {
  :deep(.btn-toggle-absence-detail-modal-displayname),
  :deep(.sdui-list-item__time) {
    text-decoration: line-through;
    color: $color-gray-300;
  }
}
</style>
